import useCrossOriginAttribute from '@aurora/shared-client/components/useCrossOriginAttribute';
import {
  setSurveyCookie,
  SURVEY_COMPLETION_COOKIE,
  SurveyCompletionCookieValues
} from '@aurora/shared-client/helpers/cookie/SurveyCookieHelper';
import React, { useState } from 'react';
import { Image, Modal, useClassNameMapper } from 'react-bootstrap';
import SurveyForm from '../SurveyForm/SurveyForm';
import useTranslation from '../../useTranslation';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import localStyles from './SurveyModal.module.pcss';
import type { FilteredSurveyWithFinalQuestionCountAndProperties } from '@aurora/shared-client/components/surveys/UseSurvey/useSurvey';
import useMeasureEnhanced from '@aurora/shared-client/components/useMeasureEnhanced';
import type { CSSPropertiesWithVars } from '@aurora/shared-client/helpers/styles/CSSPropertiesWithVarsHelper';
import ConfirmationDialog from '@aurora/shared-client/components/common/ConfirmationDialog/ConfirmationDialog';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import Icons from '@aurora/shared-client/public/static/graphics/processed/enums';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';

interface SurveyModalProps {
  /**
   * Callback to be used when the modal is closed
   */
  onClose: () => void;
  /**
   * Computed survey to be displayed
   */
  computedSurvey: FilteredSurveyWithFinalQuestionCountAndProperties;
}

/**
 * Survey Modal, displays the survey form to the user
 *
 * @param onClose the callback function to be called when the modal is closed
 * @param computedSurvey the computed survey to be displayed
 *
 * @author Adam Ayres
 */
const SurveyModal: React.FC<SurveyModalProps> = ({ onClose, computedSurvey }) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading } = useTranslation(EndUserComponent.SURVEY_MODAL);
  const [step, setStep] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const crossOrigin = useCrossOriginAttribute();

  const { ref, entry } = useMeasureEnhanced(true);

  const modalHeaderStyles: CSSPropertiesWithVars = {
    '--lia-modal-header-bg-color': computedSurvey.surveyHeaderBgColor
  };

  const modalBodyStyles: CSSPropertiesWithVars = {
    '--lia-d-modal-width': `${entry?.contentRect?.width ?? 0}px`,
    '--lia-d-survey-step': step
  };

  if (loading) {
    return null;
  }

  return (
    <>
      <Modal
        size="sm"
        show={!showConfirmation}
        onHide={onClose}
        backdrop="static"
        data-testid="SurveyModal"
        className={cx('lia-modal')}
        contentClassName={cx('overflow-hidden')}
      >
        <Modal.Header className={cx('lia-header')} style={modalHeaderStyles}>
          {computedSurvey.surveyBrandingLogo ? (
            <Image
              className={cx('lia-logo')}
              src={computedSurvey.surveyBrandingLogo.url}
              alt={computedSurvey.surveyBrandingLogo.altText}
              crossOrigin={crossOrigin(computedSurvey.surveyBrandingLogo.url)}
            />
          ) : (
            <Icon
              icon={Icons.KhorosFullLogoIcon}
              size={IconSize.FROM_CSS}
              className={cx('lia-khoros-svg')}
            />
          )}

          <Modal.Title className={cx('lia-header-title')}>{formatMessage('title')}</Modal.Title>
          <div className={cx('lia-step-indicator-container')}>
            {[...Array.from({ length: computedSurvey.finalQuestionCount }).keys()].map(i => (
              <div key={i} className={cx('lia-step-indicator', { 'lia-step-active': step > i })} />
            ))}
          </div>
        </Modal.Header>
        <Modal.Body className={cx('lia-body')} ref={ref} style={modalBodyStyles}>
          <SurveyForm
            onSubmit={() => {
              setSurveyCookie(SURVEY_COMPLETION_COOKIE, {
                timestamp: Date.now(),
                state: SurveyCompletionCookieValues.completed
              });
              onClose();
            }}
            onCancel={() => {
              setShowConfirmation(true);
            }}
            computedSurvey={computedSurvey}
            step={step}
            onStepChange={currentStep => setStep(currentStep)}
          />
        </Modal.Body>
      </Modal>
      <ConfirmationDialog
        show={showConfirmation}
        onSubmit={() => {
          onClose();
          setShowConfirmation(false);
        }}
        onCancel={() => setShowConfirmation(false)}
        titleText={formatMessage('confirmationTitle')}
        bodyText={formatMessage('confirmationMessage')}
        submitButtonText={formatMessage('confirmationSubmit')}
        submitButtonVariant={ButtonVariant.DANGER}
      />
    </>
  );
};

export default SurveyModal;
